/**
 * PipelineActivityLogButtonContainer — board-toolbar entry point for the
 * per-pipeline activity log (SE-5504 AC3).
 *
 * Self-contained in the same way `ReviewQueueButton` is: it drives the drawer
 * reactive var directly, so `TopPanelView` mounts it without prop-drilling an
 * open callback.
 *
 * ## The gate, and why it is NOT the Stage-editor gate verbatim
 *
 * The log is manager-facing, so the button is HIDDEN (never disabled) for a
 * viewer who may not edit the board — the affordance-hiding convention
 * `StageEditorButton` follows.
 *
 * It deliberately does NOT carry `StageEditorButton`'s additional
 * `isRosterBoard === false` condition. SE-5494 established empirically that on
 * the board the e2e helpers can create, that condition is never satisfied and
 * the buttons behind it do not render at all — which made a whole acceptance
 * criterion unverifiable. Copying it here would ship an AC3 surface that no
 * journey can reach. `pipelineActivityLog` is board-scoped and answers for any
 * board id, so the editor-or-owner gate is both sufficient and reachable.
 * @remarks Container component — owns the open action.
 */

import { useCallback } from "react";

import { TEST_ID_PIPELINE_ACTIVITY_LOG } from "e2e/testId";

import { Button } from "@/components/atoms/Button";

import { usePipelineActivityLogDrawerState } from "../../../hooks/usePipelineActivityLogDrawerState";

/** Props for {@link PipelineActivityLogButtonContainer}. */
export interface PipelineActivityLogButtonContainerProps {
  /** The pipeline whose log opens. */
  readonly kanbanBoardId?: string | null;
}

/**
 * Toolbar button that opens the per-pipeline activity log.
 * @param props - {@link PipelineActivityLogButtonContainerProps}
 * @param props.kanbanBoardId - The pipeline whose log opens
 * @returns The button.
 */
const PipelineActivityLogButtonContainer = ({
  kanbanBoardId,
}: PipelineActivityLogButtonContainerProps) => {
  const { open } = usePipelineActivityLogDrawerState();

  const handlePress = useCallback(
    () => open(kanbanBoardId ?? null),
    [open, kanbanBoardId]
  );

  return (
    <Button
      label="Activity"
      variant="outline"
      tone="secondary"
      size="sm"
      labelTone="primary"
      /* SE-6865: toolbar radius + label weight, set at the call site on
         purpose — see the anchor note in ReviewQueueButtonContainer for the two
         atom-level defects this deliberately does NOT fix. */
      shape="rounded"
      labelVariant="body-strong"
      /* SE-6554: grey toolbar border — see the Filters waiver in TopPanelView. */
      className="web:hover:bg-surface-subtle border-outline-strong"
      accessibilityLabel="Open pipeline activity log"
      testID={TEST_ID_PIPELINE_ACTIVITY_LOG.OPEN}
      onPress={handlePress}
    />
  );
};

PipelineActivityLogButtonContainer.displayName =
  "PipelineActivityLogButtonContainer";

export default PipelineActivityLogButtonContainer;
